import type { MeasureMetric } from '@erp/shared';
import { api } from './client';
import type { UnitOfMeasure, VatRate } from './catalogs';

export interface PriceListSection {
  id: string;
  name: string;
  parentId: string | null;
}

export interface PriceListSectionInput {
  name: string;
  parentId?: string | null;
}

export interface PriceListItem {
  id: string;
  name: string;
  code: string | null;
  sectionId: string | null;
  section: PriceListSection | null;
  unitId: string;
  unit: UnitOfMeasure;
  vatRateId: string | null;
  vatRate: VatRate | null;
  price: string;
  measureMetric: MeasureMetric | null;
  wastePercent: string | null;
  isActive: boolean;
}

export interface PriceListItemInput {
  name: string;
  code?: string | null;
  sectionId?: string | null;
  unitId: string;
  vatRateId?: string | null;
  price: string;
  measureMetric?: MeasureMetric | null;
  wastePercent?: string | null;
  isActive?: boolean;
}

export interface PriceListItemListResponse {
  items: PriceListItem[];
  total: number;
  page: number;
  pageSize: number;
}

export interface PriceListItemListParams {
  search?: string;
  sectionId?: string;
  includeInactive?: boolean;
  page?: number;
  pageSize?: number;
}

function buildQuery(params: PriceListItemListParams): string {
  const search = new URLSearchParams();
  if (params.search) search.set('search', params.search);
  if (params.sectionId) search.set('sectionId', params.sectionId);
  if (params.includeInactive) search.set('includeInactive', 'true');
  if (params.page !== undefined) search.set('page', String(params.page));
  if (params.pageSize !== undefined) search.set('pageSize', String(params.pageSize));
  const query = search.toString();
  return query ? `?${query}` : '';
}

export const priceListItemsApi = {
  list: (params: PriceListItemListParams = {}) =>
    api.get<PriceListItemListResponse>(`/price-list-items${buildQuery(params)}`),

  /**
   * Все расценки постранично. Сервер отдаёт не больше 200 за запрос, а список
   * отсортирован по названию — одним запросом смета теряла бы всё, что по
   * алфавиту дальше двухсотой позиции, и такие работы нельзя было бы выбрать.
   */
  listAll: async (params: Omit<PriceListItemListParams, 'page' | 'pageSize'> = {}): Promise<PriceListItem[]> => {
    const pageSize = 200;
    const items: PriceListItem[] = [];
    for (let page = 1; ; page++) {
      const res = await api.get<PriceListItemListResponse>(
        `/price-list-items${buildQuery({ ...params, page, pageSize })}`,
      );
      items.push(...res.items);
      if (res.items.length === 0 || items.length >= res.total) return items;
    }
  },

  get: (id: string) => api.get<{ item: PriceListItem }>(`/price-list-items/${id}`),

  create: (data: PriceListItemInput) => api.post<{ item: PriceListItem }>('/price-list-items', data),

  update: (id: string, data: Partial<PriceListItemInput>) =>
    api.patch<{ item: PriceListItem }>(`/price-list-items/${id}`, data),

  remove: (id: string) => api.delete<void>(`/price-list-items/${id}`),

  activate: (id: string) => api.post<{ item: PriceListItem }>(`/price-list-items/${id}/activate`),

  deactivate: (id: string) => api.post<{ item: PriceListItem }>(`/price-list-items/${id}/deactivate`),
};

export const priceListSectionsApi = {
  list: () => api.get<{ items: PriceListSection[] }>('/price-list-sections'),

  create: (data: PriceListSectionInput) => api.post<{ section: PriceListSection }>('/price-list-sections', data),

  update: (id: string, data: Partial<PriceListSectionInput>) =>
    api.patch<{ section: PriceListSection }>(`/price-list-sections/${id}`, data),

  remove: (id: string) => api.delete<void>(`/price-list-sections/${id}`),
};
